'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { DOCS_URL } from '@/lib/constants/docs';
import { Button } from '@/lib/ui-kit-v2/button';
import { getAnalyticsConsent, setAnalyticsConsent } from './consent';

/**
 * Asks the visitor about analytics once, on any zone.
 *
 * Consent is opt-out (see `consent.ts`): tracking is already on while the
 * banner is up, and "Decline" is what turns it off. The banner only records
 * the decision through the consent seam — PostHog reacts to it in
 * `PostHogInit`, so this component knows nothing about the SDK.
 *
 * The cookie is read after mount, never during render: the server has no
 * cookie, and a server/client mismatch would flash the banner on every load.
 */
export function AnalyticsConsentBanner() {
  const t = useTranslations('analyticsConsent');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(getAnalyticsConsent() === undefined);
  }, []);

  if (!open) return null;

  const decide = (granted: boolean) => {
    setAnalyticsConsent(granted);
    setOpen(false);
  };

  return (
    <section
      aria-label={t('title')}
      // Bottom-right corner from `sm` up; phones get the full width. Below 1024px
      // the mobile nav (60px) is visible, so the card sits above it.
      className="fixed inset-x-4 bottom-[76px] z-50 animate-consent-banner-in rounded-xl border border-v2-border bg-v2-surface-secondary p-5 text-v2-text-primary shadow-lg motion-reduce:animate-none sm:inset-x-auto sm:right-6 sm:w-[420px] sm:p-6 min-[1024px]:bottom-6"
    >
      <p className="text-lg font-semibold leading-6">{t('title')}</p>
      <p className="mt-2 text-sm leading-5 text-v2-text-secondary">
        {t.rich('message', {
          policy: (chunks) => (
            <a
              href={DOCS_URL.cookiePolicy}
              target="_blank"
              rel="noopener noreferrer"
              className="text-v2-text-primary underline underline-offset-2 hover:opacity-80"
            >
              {chunks}
            </a>
          ),
        })}
      </p>
      <div className="mt-5 flex gap-3">
        <Button appearance="accent" className="flex-1" onClick={() => decide(true)}>
          {t('accept')}
        </Button>
        <Button appearance="outline" className="flex-1" onClick={() => decide(false)}>
          {t('decline')}
        </Button>
      </div>
    </section>
  );
}
